import { StringContext } from "../../core/StringContext";
import { StringModule } from "../../core/StringModule";

enum DeviceType {
  Mobile,
  Tablet,
  Laptop,
  Desktop,
}

interface DeviceQueryConfig {
  min?: number;
  max?: number;
  enable?: boolean;
}

interface QueryConfig {
  mobile?: DeviceQueryConfig;
  tablet?: DeviceQueryConfig;
  laptop?: DeviceQueryConfig;
  desktop?: DeviceQueryConfig;
}

class StringResponsiveQueryDevice {
  public min?: number = undefined;
  public max?: number = undefined;
  public enable: boolean = true;

  constructor(config?: DeviceQueryConfig) {
    this.min = config?.min;
    this.max = config?.max;
    this.enable = config?.enable ?? true;
  }

  setEnable(enable: boolean = true) {
    this.enable = enable;
  }
  setRange(min?: number, max?: number) {
    this.min = min ?? undefined;
    this.max = max ?? undefined;
  }

  get mediaQuery(): string {
    let query = "screen";
    if (this.min) {
      query += ` and (min-width: ${this.min}px)`;
    }
    if (this.max) {
      query += ` and (max-width: ${this.max}px)`;
    }
    return query;
  }
}

export class StringResponsive extends StringModule {
  private queries: { [key in DeviceType]: StringResponsiveQueryDevice } = {
    [DeviceType.Mobile]: new StringResponsiveQueryDevice({ max: 359 }),
    [DeviceType.Tablet]: new StringResponsiveQueryDevice({
      min: 360,
      max: 1023,
    }),
    [DeviceType.Laptop]: new StringResponsiveQueryDevice({
      min: 1024,
      max: 1365,
    }),
    [DeviceType.Desktop]: new StringResponsiveQueryDevice({ min: 1366 }),
  };

  private isMobileMedia: boolean = false;
  private isTabletMedia: boolean = false;
  private isLaptopMedia: boolean = false;
  private isDesktopMedia: boolean = false;

  private matchMedias: { [key in DeviceType]: MediaQueryList } = {
    [DeviceType.Mobile]: window.matchMedia(this.queries[DeviceType.Mobile].mediaQuery),
    [DeviceType.Tablet]: window.matchMedia(this.queries[DeviceType.Tablet].mediaQuery),
    [DeviceType.Laptop]: window.matchMedia(this.queries[DeviceType.Laptop].mediaQuery),
    [DeviceType.Desktop]: window.matchMedia(this.queries[DeviceType.Desktop].mediaQuery),
  };

  constructor(context: StringContext) {
    super(context);
    this._type = 2;
  }

  onConnect() {}

  onInit(): void {
    if (this.settings != null) {
      if (this.settings["settings"] != null) {
        let config = this.settings["settings"];
        if (config.mobile) {
          this.queries[DeviceType.Mobile].enable = true;
          this.queries[DeviceType.Mobile].setRange(
            config.mobile.min == undefined ? null : config.mobile.min,
            config.mobile.max ?? null
          );
          this.matchMedias[DeviceType.Mobile] = window.matchMedia(
            this.queries[DeviceType.Mobile].mediaQuery
          );
        } else {
          this.queries[DeviceType.Mobile].enable = false;
        }

        if (config.tablet) {
          this.queries[DeviceType.Tablet].enable = true;
          this.queries[DeviceType.Tablet].setRange(
            config.tablet.min == undefined ? null : config.tablet.min,
            config.tablet.max ?? null
          );
          this.matchMedias[DeviceType.Tablet] = window.matchMedia(
            this.queries[DeviceType.Tablet].mediaQuery
          );
        } else {
          this.queries[DeviceType.Tablet].enable = false;
        }

        if (config.laptop) {
          this.queries[DeviceType.Laptop].enable = true;
          this.queries[DeviceType.Laptop].setRange(
            config.laptop.min == undefined ? null : config.laptop.min,
            config.laptop.max ?? null
          );
          this.matchMedias[DeviceType.Laptop] = window.matchMedia(
            this.queries[DeviceType.Laptop].mediaQuery
          );
        } else {
          this.queries[DeviceType.Laptop].enable = false;
        }

        if (config.desktop) {
          this.queries[DeviceType.Desktop].enable = true;
          this.queries[DeviceType.Desktop].setRange(
            config.desktop.min == undefined ? null : config.desktop.min,
            config.desktop.max ?? null
          );
          this.matchMedias[DeviceType.Desktop] = window.matchMedia(
            this.queries[DeviceType.Desktop].mediaQuery
          );
        } else {
          this.queries[DeviceType.Desktop].enable = false;
        }
      }
    }
    this.updateElements();
  }

  onResize(): void {
    this.updateElements();
  }

  private updateElements() {
    const isMobileMedia =
      this.matchMedias[DeviceType.Mobile].matches && this.queries[DeviceType.Mobile].enable;
    const isTabletMedia =
      this.matchMedias[DeviceType.Tablet].matches && this.queries[DeviceType.Tablet].enable;
    const isLaptopMedia =
      this.matchMedias[DeviceType.Laptop].matches && this.queries[DeviceType.Laptop].enable;
    const isDesktopMedia =
      this.matchMedias[DeviceType.Desktop].matches && this.queries[DeviceType.Desktop].enable;

    if (this.isMobileMedia != isMobileMedia) {
      this.events.emit("screen:mobile", isMobileMedia);
    }
    if (this.isTabletMedia != isTabletMedia) {
      this.events.emit("screen:tablet", isTabletMedia);
    }
    if (this.isLaptopMedia != isLaptopMedia) {
      this.events.emit("screen:laptop", isLaptopMedia);
    }
    if (this.isDesktopMedia != isDesktopMedia) {
      this.events.emit("screen:desktop", isDesktopMedia);
    }

    this.isMobileMedia = isMobileMedia;
    this.isTabletMedia = isTabletMedia;
    this.isLaptopMedia = isLaptopMedia;
    this.isDesktopMedia = isDesktopMedia;

    const elements = document.querySelectorAll(
      "[string-mobile], [string-tablet], [string-laptop], [string-desktop]"
    );

    elements.forEach((element: any) => {
      let showElement = false;

      if (element.hasAttribute("string-mobile") && isMobileMedia) {
        showElement = true;
      }
      if (element.hasAttribute("string-tablet") && isTabletMedia) {
        showElement = true;
      }
      if (element.hasAttribute("string-laptop") && isLaptopMedia) {
        showElement = true;
      }
      if (element.hasAttribute("string-desktop") && isDesktopMedia) {
        showElement = true;
      }

      if (showElement) {
        element.style.display = null;
      } else {
        element.style.display = `none`;
      }
    });
  }
}
